import type { NormalizedTxn, PatternBased, PatternBranch, PatternMeasure, RuleBased, RuleBranch, RuleSet } from '@kkp-fraud/shared';

/**
 * Action rows ("branches") of a rule-based rule.
 *
 * A rule may carry several `{ action, clauses }` rows, first match wins — the engine
 * evaluates them natively (docs/engine-rows-semantics.md). `action` + `clauses` ALWAYS
 * mirror `branches[0]` (validation refuses anything else), and a rule with one row carries
 * no `branches` property at all — every document released before this existed round-trips
 * byte-identical, and its release diff stays empty.
 */

/** The rows of a rule: `branches` when present, otherwise the single row it has always had. */
export function branchesOf(rule: RuleBased): RuleBranch[] {
  if (Array.isArray(rule.branches) && rule.branches.length > 0) return rule.branches;
  return [{ action: rule.action, clauses: rule.clauses }];
}

/**
 * Writes rows back onto a rule. `action`/`clauses` come from row 1; `branches` is kept only
 * when there is more than one row, otherwise the property is deleted.
 */
export function withBranches(rule: RuleBased, branches: RuleBranch[]): RuleBased {
  const first = branches[0] ?? { action: rule.action, clauses: rule.clauses };
  const next: RuleBased = { ...rule, action: first.action, clauses: first.clauses };
  if (branches.length > 1) next.branches = branches;
  else delete next.branches;
  return next;
}

/**
 * Action rows of a pattern-based rule. Each row has an action and one or more MEASURES —
 * whole aggregate conditions (scope, event, aggregate, aggregate field, window, threshold,
 * filters) — joined by the row's `logic` (AND unless it says OR). The rule-level fields
 * ALWAYS mirror `branches[0].measures[0]` and `branches[0].action`, and a rule with one row
 * of one measure carries no `branches` property.
 *
 * Older rows are read as one measure: a row without `measures` (`{ action, threshold,
 * filters }`) takes each missing counting field from the rule level.
 */
export function patternBranchesOf(rule: PatternBased): PatternBranch[] {
  if (Array.isArray(rule.branches) && rule.branches.length > 0) return rule.branches.map((row) => filledRow(rule, row));
  return [{ action: rule.action, measures: [measureOf(rule)] }];
}

/**
 * The measure fields of `from` in canonical order; `aggregateField` only when it is set.
 * `filters` is never dropped: an absent one becomes `[]` — both engines' validators refuse a
 * measure without `filters` (docs/engine-rows-semantics.md §5).
 */
function measureOf(from: PatternMeasure): PatternMeasure {
  const m: PatternMeasure = {
    scope: from.scope,
    event: from.event,
    aggregate: from.aggregate,
    windowSeconds: from.windowSeconds,
    filters: from.filters ?? [],
  };
  if (from.threshold !== undefined) m.threshold = from.threshold;
  if (from.aggregateField !== undefined) m.aggregateField = from.aggregateField;
  // 2c rule sets — a `set_fired` measure names its set
  if (from.ruleSetId !== undefined) m.ruleSetId = from.ruleSetId;
  return m;
}

// ---- 2a expressions ----------------------------------------------------------------
/**
 * An EXPRESSION row (Wave 2a, docs/engine-rows-semantics.md §7): it carries `conditions` over
 * its named measures, has no `logic` and no measure `threshold`. It is never re-shaped onto
 * another row (and no row is re-shaped onto it) — its structure is its own.
 */
export function isExpressionRow(row: PatternBranch | undefined): boolean {
  return row !== undefined && row.conditions !== undefined;
}

/** The scopes and aggregates the RULE LEVEL (and a threshold row) may use — the engine's older vocabulary. */
const RULE_LEVEL_SCOPES: readonly string[] = ['card_id', 'customer_id', 'merchant_id'];
const RULE_LEVEL_AGGREGATES: readonly string[] = ['count', 'sum', 'distinct'];

/**
 * The rule-level counting fields of a rule whose row 1 is an expression row. The engine only
 * compares the rule-level `action` with row 1's then (`pb_mirror_action`) but still types and
 * checks the counting fields in the older vocabulary, so they are row 1's first measure where
 * that fits and a harmless stand-in where it does not: scope `terminal_id` → `card_id`, a Wave
 * 2a aggregate → `count` (no field), threshold 1. Key order of `rule` is kept (a no-op stays
 * byte-identical) and `aggregateField` is deleted, never set to `undefined`.
 */
function mirrorExpression(rule: PatternBased, row: PatternBranch): PatternBased {
  const m = row.measures[0];
  const next: PatternBased = { ...rule, action: row.action };
  if (!m) return next;
  const aggregate = RULE_LEVEL_AGGREGATES.includes(m.aggregate) ? m.aggregate : 'count';
  next.scope = RULE_LEVEL_SCOPES.includes(m.scope) ? m.scope : 'card_id';
  next.event = m.event;
  next.aggregate = aggregate;
  next.threshold = 1;
  next.windowSeconds = m.windowSeconds;
  next.filters = m.filters ?? [];
  if (aggregate !== 'count' && m.aggregateField !== undefined) next.aggregateField = m.aggregateField;
  else delete next.aggregateField;
  mirrorRuleSetId(next, m);
  return next;
}

/**
 * 2c rule sets — the rule level mirrors row 1's first measure's `ruleSetId` (`pb_mirror`): set
 * when the measure names a set, the key deleted (never `undefined`) when it does not.
 */
function mirrorRuleSetId(next: PatternBased, m: PatternMeasure): void {
  if (m.ruleSetId !== undefined) next.ruleSetId = m.ruleSetId;
  else delete next.ruleSetId;
}

/**
 * A stored row as a complete row. A row with measures is returned as it is. An older row
 * becomes one measure: its own fields where it has them, the rule's for the rest.
 * `aggregateField` comes from the rule only when the row has no `aggregate` of its own —
 * a row that says `count` must not inherit the rule's field.
 */
function filledRow(rule: PatternBased, row: PatternBranch): PatternBranch {
  if (Array.isArray(row.measures) && row.measures.length > 0) return row;
  const old = row as unknown as Partial<PatternMeasure>;
  const measure: PatternMeasure = {
    scope: old.scope ?? rule.scope,
    event: old.event ?? rule.event,
    aggregate: old.aggregate ?? rule.aggregate,
    windowSeconds: old.windowSeconds ?? rule.windowSeconds,
    threshold: old.threshold ?? rule.threshold,
    filters: old.filters ?? rule.filters ?? [],
  };
  // 2a expressions — an older row never carries conditions (the engine refuses it); keep them if it does
  const out0: PatternBranch = { action: row.action, measures: [measure] };
  if (row.conditions !== undefined) out0.conditions = row.conditions;
  const field = old.aggregate === undefined ? (old.aggregateField ?? rule.aggregateField) : old.aggregateField;
  if (field !== undefined) measure.aggregateField = field;
  // 2c rule sets — the set goes with the event: the rule's only when the event is the rule's
  const set = old.event === undefined ? (old.ruleSetId ?? rule.ruleSetId) : old.ruleSetId;
  if (set !== undefined) measure.ruleSetId = set;
  const out: PatternBranch = out0;
  if (row.logic !== undefined) out.logic = row.logic;
  return out;
}

/**
 * A measure with no `aggregateField` KEY when it has none — never `aggregateField: undefined` —
 * and always a `filters` array (`[]` when absent). A well-formed measure is returned as it is.
 */
function cleanMeasure(m: PatternMeasure): PatternMeasure {
  const dropField = m.aggregateField === undefined && 'aggregateField' in m;
  const noFilters = m.filters === undefined || m.filters === null;
  // 2c rule sets — nor a `ruleSetId: undefined` key
  const dropSet = m.ruleSetId === undefined && 'ruleSetId' in m;
  if (!dropField && !noFilters && !dropSet) return m;
  const out = { ...m };
  if (dropField) delete out.aggregateField;
  if (noFilters) out.filters = [];
  if (dropSet) delete out.ruleSetId;
  return out;
}

/**
 * A row as it is stored: measures cleaned, no older-shape `threshold`/`filters` beside them,
 * and no `logic` unless it is OR (absent means AND).
 */
function cleanRow(row: PatternBranch): PatternBranch {
  const out: PatternBranch = { ...row, measures: row.measures.map(cleanMeasure) };
  delete out.threshold;
  delete out.filters;
  // 2a expressions — an expression row's conditions say how they combine; it has no `logic`
  if (out.logic !== 'OR' || isExpressionRow(out)) delete out.logic;
  return out;
}

/** Does this list of rows need a `branches` property: several rows, several measures, or OR? */
export function needsPatternBranches(rows: readonly PatternBranch[]): boolean {
  // 2a expressions — conditions exist only on an action row
  return rows.length > 1 || rows.some((row) => isExpressionRow(row) || row.measures.length > 1 || (row.logic ?? 'AND') !== 'AND');
}

/**
 * `rule` as a one-measure rule: `measure` and `action` written onto the rule-level fields
 * and no `branches`. What one canvas shows, what one table line says.
 * `aggregateField` is removed, never set to `undefined`, when the measure has none.
 */
export function patternMeasureRule(rule: PatternBased, measure: PatternMeasure, action: PatternBased['action']): PatternBased {
  const one = mirror(rule, measure, action);
  delete one.branches;
  return one;
}

/** `patternMeasureRule` for a row's first measure. */
export function patternRowRule(rule: PatternBased, row: PatternBranch): PatternBased {
  if (isExpressionRow(row)) {
    const one = mirrorExpression(rule, row);
    delete one.branches;
    return one;
  }
  return patternMeasureRule(rule, row.measures[0] ?? measureOf(rule), row.action);
}

/**
 * The rule-level fields set from one measure (key order of `rule` kept, so a no-op is
 * byte-identical). `filters` defaults to `[]` when the measure has none — never dropped.
 */
function mirror(rule: PatternBased, m: PatternMeasure, action: PatternBased['action']): PatternBased {
  const next: PatternBased = {
    ...rule,
    scope: m.scope,
    event: m.event,
    aggregate: m.aggregate,
    threshold: m.threshold ?? rule.threshold,
    windowSeconds: m.windowSeconds,
    filters: m.filters ?? [],
    action,
  };
  if (m.aggregateField !== undefined) next.aggregateField = m.aggregateField;
  else delete next.aggregateField;
  mirrorRuleSetId(next, m);
  return next;
}

/**
 * `withBranches` for a pattern rule: row 1's first measure and action mirrored onto the
 * rule; `branches` kept only when `needsPatternBranches`, otherwise the property is deleted.
 */
export function withPatternBranches(rule: PatternBased, branches: PatternBranch[]): PatternBased {
  const rows = branches.map(cleanRow);
  const first = rows[0];
  const next = isExpressionRow(first)
    ? mirrorExpression(rule, first!)
    : mirror(rule, first?.measures[0] ?? measureOf(rule), first?.action ?? rule.action);
  if (needsPatternBranches(rows)) next.branches = rows;
  else delete next.branches;
  return next;
}

// ---- adding rows and measures ---------------------------------------------------
/*
 * Every action row has its own structure: its own clauses (field, operator, AND/OR, how many)
 * for a rule-based rule, its own measures (scope, event, aggregate, window, filters) for a
 * pattern rule. The engine does not ask rows to share a structure — only the rule level must
 * mirror row 1 (`rb_mirror` / `pb_mirror`, docs/engine-rows-semantics.md §5), which
 * `withBranches` / `withPatternBranches` write on every edit. A NEW row starts as a copy of
 * row 1 for convenience; after that it is edited on its own.
 */

/** A deep copy of a measure. JSON would turn a NaN (emptied) window or threshold into null. */
export const copyMeasure = (m: PatternMeasure): PatternMeasure => {
  const copy: PatternMeasure = { ...(JSON.parse(JSON.stringify(m)) as PatternMeasure), windowSeconds: m.windowSeconds };
  if (m.threshold !== undefined) copy.threshold = m.threshold;
  return copy;
};

/** The id older dry runs gave row n of a multi-row rule (`RB-01#2`); still parsed by the dashboards. */
export const BRANCH_ID_SEPARATOR = '#';

/** `RB-01#2` → `{ ruleId: 'RB-01', branch: 2 }`; `RB-01` → `{ ruleId: 'RB-01', branch: 1 }`. */
export function branchOfHitId(id: string): { ruleId: string; branch: number } | null {
  if (!id) return null;
  const at = id.lastIndexOf(BRANCH_ID_SEPARATOR);
  if (at < 0) return { ruleId: id, branch: 1 };
  const ruleId = id.slice(0, at);
  const branch = Number(id.slice(at + 1));
  if (!ruleId || !Number.isInteger(branch) || branch < 1) return null;
  return { ruleId, branch };
}

/**
 * Keeps every existing `requires` entry and adds `fraud_score` when row 1 reads it. Only
 * row 1: `requires` skips the WHOLE rule, so a field read only by a later row must not
 * silence the rows above it (a null field simply never satisfies that row's clause).
 */
export function deriveRequires(rule: RuleBased): RuleBased {
  const first = branchesOf(rule)[0];
  const reads = (first?.clauses ?? []).some((c) => c.field === 'fraud_score');
  const requires = rule.requires ?? [];
  if (!reads || requires.includes('fraud_score')) return rule;
  return { ...rule, requires: [...requires, 'fraud_score'] };
}

/** A rule set with `rule` replaced by id (or appended) — a copy; the input is not touched. */
export function upsertRule(rs: RuleSet, kind: 'ruleBased' | 'patternBased', rule: RuleBased | PatternBased): RuleSet {
  const list = [...((rs[kind] ?? []) as (RuleBased | PatternBased)[])];
  const at = list.findIndex((r) => r.id === rule.id);
  if (at >= 0) list[at] = rule;
  else list.push(rule);
  return { ...rs, [kind]: list } as RuleSet;
}

/**
 * The scopes that made the engine skip this pattern rule on `txn`, or `[]` when it was
 * evaluated. The engine skips a pattern rule only when NO measure of any row has its scope
 * value (docs/engine-rows-semantics.md §2.2) — a missing scope in an OR row is just a measure
 * that is not satisfied. For a single-row rule that is exactly "the rule's scope is missing".
 */
export function skippedPatternScopes(rule: PatternBased, txn: NormalizedTxn): string[] {
  const scopes = [...new Set(patternBranchesOf(rule).flatMap((row) => row.measures.map((m) => m.scope)))];
  const absent = (scope: string) => {
    const v = txn[scope as keyof NormalizedTxn];
    return v === null || v === undefined || v === '';
  };
  return scopes.every(absent) ? scopes : [];
}
